import type { RouteObject } from 'react-router-dom';
import { Navigate } from 'react-router-dom';
import { ProtectedLayout } from '@/admin/components/layout/protected-layout';
import { DashboardPage } from '@/admin/pages/dashboard';
import { UsersPage } from '@/admin/pages/users';
import { DevicesPage } from '@/admin/pages/devices';
import { UsagePage } from '@/admin/pages/usage';
import { ChatsPage } from '@/admin/pages/chats';
import { ChatDetailPage } from '@/admin/pages/chat-detail';
import { ConversationDetailPage } from '@/admin/pages/conversation-detail';
import { AuditPage } from '@/admin/pages/audit';
import { SkillsPage } from '@/admin/pages/skills';
import { SkillDetailPage } from '@/admin/pages/skill-detail';
import { SkillCommentsPage } from '@/admin/pages/skill-comments';
import { BlogsPage } from '@/admin/pages/blogs';
import { BlogDetailPage } from '@/admin/pages/blog-detail';
import { PlansPage } from '@/admin/pages/plans';
import { AddonsPage } from '@/admin/pages/addons';
import { RedeemCodesPage } from '@/admin/pages/redeem-codes';
import { InviteCodesPage } from '@/admin/pages/invite-codes';
import { LoginPage } from '@/admin/pages/login';
import { LoginConfirmPage } from '@/admin/pages/login-confirm';
import { TelemetryArtifactPage } from '@/admin/pages/telemetry-artifact';
import { BenchmarkLayout } from '@/admin/components/benchmark/layout';
import { BenchmarkDashboard } from '@/admin/pages/benchmark/stats';
import { EvalsPage } from '@/admin/pages/evals';
import { EvalDetailPage } from '@/admin/pages/eval-detail';
import { GalleryPage } from '@/admin/pages/gallery';
import {
  PlatformModelsPage,
  PlatformProvidersPage,
  PlatformRoutesPage,
  PlatformServiceBindingsPage,
  WillowInitialAgentsPage,
  WillowTemplatesPage,
} from '@/admin/pages/llm-settings';
import {
  WillowGroupsPage,
  WillowOperationsPage,
} from '@/admin/pages/willow-groups-operations';

/**
 * Application routes as RouteObject array for createBrowserRouter.
 * This format is required for data router features like useBlocker.
 */
export const routes: RouteObject[] = [
  // Public routes
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/login/confirm',
    element: <LoginConfirmPage />,
  },
  // Protected routes
  {
    path: '/',
    element: <ProtectedLayout />,
    children: [
      {
        index: true,
        element: <DashboardPage />,
      },
      {
        path: 'users',
        element: <UsersPage />,
      },
      {
        path: 'devices',
        element: <DevicesPage />,
      },
      {
        path: 'usage',
        element: <UsagePage />,
      },
      {
        path: 'llm',
        element: <Navigate to="/platform/providers" replace />,
      },
      {
        path: 'platform',
        element: <Navigate to="/platform/providers" replace />,
      },
      {
        path: 'platform/providers',
        element: <PlatformProvidersPage />,
      },
      {
        path: 'platform/models',
        element: <PlatformModelsPage />,
      },
      {
        path: 'platform/routes',
        element: <PlatformRoutesPage />,
      },
      {
        path: 'platform/service-bindings',
        element: <PlatformServiceBindingsPage />,
      },
      {
        path: 'willow',
        element: <Navigate to="/willow/groups" replace />,
      },
      {
        path: 'willow/groups',
        element: <WillowGroupsPage />,
      },
      {
        path: 'willow/operations',
        element: <WillowOperationsPage />,
      },
      {
        path: 'chats',
        element: <ChatsPage />,
      },
      {
        path: 'chats/:sessionId',
        element: <ChatDetailPage />,
      },
      {
        path: 'conversations',
        element: <ChatsPage />,
      },
      {
        path: 'conversations/:conversationId',
        element: <ConversationDetailPage />,
      },
      {
        path: 'willow/templates',
        element: <WillowTemplatesPage />,
      },
      {
        path: 'willow/initial-agents',
        element: <WillowInitialAgentsPage />,
      },
      {
        path: 'audit',
        element: <AuditPage />,
      },
      {
        path: 'skills',
        element: <SkillsPage />,
      },
      {
        path: 'skills/:id',
        element: <SkillDetailPage />,
      },
      {
        path: 'skill-comments',
        element: <SkillCommentsPage />,
      },
      {
        path: 'blogs',
        element: <BlogsPage />,
      },
      {
        path: 'blogs/:id',
        element: <BlogDetailPage />,
      },
      {
        path: 'plans',
        element: <PlansPage />,
      },
      {
        path: 'addons',
        element: <AddonsPage />,
      },
      {
        path: 'redeem-codes',
        element: <RedeemCodesPage />,
      },
      {
        path: 'invite-codes',
        element: <InviteCodesPage />,
      },
      {
        path: 'telemetry/artifacts/:id',
        element: <TelemetryArtifactPage />,
      },
      // Benchmark routes with nested layout
      {
        path: 'benchmark',
        element: <BenchmarkLayout />,
        children: [
          {
            index: true,
            element: <BenchmarkDashboard />,
          },
          {
            path: '*',
            element: <Navigate to="/benchmark" replace />,
          },
        ],
      },
      {
        path: 'evals',
        element: <EvalsPage />,
      },
      {
        path: 'evals/:experimentId',
        element: <EvalDetailPage />,
      },
      {
        path: 'gallery',
        element: <GalleryPage />,
      },
    ],
  },
];
